import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ClientSideRowModelModule, type ColDef, ModuleRegistry } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { getMainAxios } from '../utils/util';
import StrategySlotManager from './StrategySlotManager';
import { useGridEdits } from './gridEdit';
import {
  page, card, cardGrid, titleRow, gridTitle, gridHint, toolbarLabel,
  btnPrimary, selectSm, emptyMsg, colors, won, agGridBox,
} from '../styles/fdsTheme';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

/**
 * 계좌·전략 관리 (2026-11-03) — 편집 그리드 표준(fdsapp-ui §14, gridEdit.ts).
 *
 *   위  계좌 그리드 — 조회·추가·수정·정지를 여기서 다 한다 (API 키도 칸에서 새로 입력)
 *   아래 전략 슬롯 그리드 — 위에서 클릭한 계좌의 슬롯 추가·수정·삭제 (StrategySlotManager pfId=)
 *
 * 모달은 여러 테이블을 한 번에 바꾸는 작업에만 쓴다: 새 기간으로 갱신(계좌+슬롯+권한 복사) · 그룹 추가·수정 · 변경 이력 보기.
 * 사용자·비밀번호는 사용자 단위라 Admin > 계정관리에서 한다.
 *
 * 계좌는 "id 이름"(예: 8 모의주식-2611)으로 부른다. 주식/선물은 계좌번호로 추정하지 않고 직접 고른다.
 * 계좌는 물리 삭제하지 않는다(CLAUDE.md §6) — [삭제] = 정지.
 * 설계: docs/design/20261103-사용자-계좌-API키-전략-통합관리.md
 */

interface Period {
  pfId: number; idNm?: string | null; grpId?: number | null; accountNo: string; htsId?: string | null; broker: string;
  isReal: boolean; acntType: string; isActive: boolean; initD: string; endD?: string | null;
  daysLeft?: number | null; initAmt: number; prevPfId?: number | null; appKey?: string | null;
  tokenToday?: boolean | null; nSlots: number; allocSum: number;
}
interface Group {
  id: number; userId: number; name: string; broker: string; isReal: boolean; acntType: string;
  memo?: string | null; isActive: boolean; periods: Period[];
}
interface UserRow { id: number; email: string; name: string; role: number; }
interface GrantRow { id: number; userId: number; pfId: number; pfStratId?: number | null; slotName?: string | null; canOrder: boolean; }
interface Tree { users: UserRow[]; groups: Group[]; ungrouped: Period[]; grants: GrantRow[]; }
interface LogRow { id: number; field: string; oldVal?: string | null; newVal?: string | null; at: string; memo?: string | null; by: string; }
/** 그리드 한 줄 = 계좌 한 기간. appKeyNew/appSecretNew = 새로 넣은 키(빈 값이면 그대로) */
interface Row extends Period { ownerNm: string; ownerId: number; userNms: string; appKeyNew?: string; appSecretNew?: string; }

const ROLE_NM: Record<number, string> = { 0: 'None', 5: 'Viewer', 10: 'User', 15: 'API주문', 20: 'Intern', 50: 'Manager', 90: 'Dev' };
const today = () => new Date().toISOString().slice(0, 10);
const addMonths = (d: string, m: number) => { const x = new Date(d); x.setMonth(x.getMonth() + m); return x.toISOString().slice(0, 10); };
const errMsg = (e: any, dflt: string) => e?.response?.data?.error || e?.response?.data?.message || dflt;
/** 계좌 부르는 이름 — "8 모의주식-2611" */
const acctNm = (p: { pfId: number; idNm?: string | null }) => p.pfId < 0 ? `새 계좌${p.idNm ? ` ${p.idNm}` : ''}` : p.idNm ? `${p.pfId} ${p.idNm}` : `${p.pfId}`;
/** 다음 기간 이름 제안 — "모의주식-2608" → "모의주식-{시작 yyMM}" */
const nextNm = (prev: string | null | undefined, initD: string) => {
  const yymm = initD.slice(2, 4) + initD.slice(5, 7);
  const base = (prev ?? '').replace(/-\d{4}$/, '');
  return base ? `${base}-${yymm}` : '';
};

const btn = (bg: string): React.CSSProperties => ({ ...btnPrimary, backgroundColor: bg });
const badge = (fg: string, bg: string): React.CSSProperties => ({ fontSize: '11px', fontWeight: 600, color: fg, backgroundColor: bg, borderRadius: '3px', padding: '1px 6px' });
const inp: React.CSSProperties = { fontSize: '12px', padding: '4px 6px', border: `1px solid ${colors.inputBorder}`, borderRadius: '3px', width: '100%', boxSizing: 'border-box' };
const lbl: React.CSSProperties = { fontSize: '11px', color: colors.muted, marginBottom: '2px', display: 'block' };

/** 브로커 · 실전/모의 — 그룹에서 정해지므로 그리드에서 고치지 않는다 */
const BrokerBadge: React.FC<{ p: { broker: string; isReal: boolean } }> = ({ p }) => (
  <span style={{ display: 'inline-flex', gap: 4 }}>
    <span style={badge(p.broker === 'sim' ? '#7c3aed' : p.broker === 'kiwoom' ? '#0891b2' : colors.title, colors.dividerLight)}>{p.broker.toUpperCase()}</span>
    {p.broker !== 'sim' && <span style={badge(p.isReal ? colors.up : '#16a34a', p.isReal ? colors.buyBg : '#dcfce7')}>{p.isReal ? '실전' : '모의'}</span>}
  </span>
);

/** 입력 필드 정의 — 모달은 갱신·그룹에만 */
type Field = { k: string; label: string; type?: 'text' | 'date' | 'number' | 'select' | 'check' | 'password'; opts?: [string, string][]; full?: boolean; hint?: string };
interface FormState { title: string; note?: string; fields: Field[]; values: Record<string, any>; submit: (v: Record<string, any>) => Promise<void>; }

const FormModal: React.FC<{ f: FormState; onClose: () => void }> = ({ f, onClose }) => {
  const [v, setV] = useState<Record<string, any>>(f.values);
  const [busy, setBusy] = useState(false);
  const set = (k: string, x: any) => setV(o => ({ ...o, [k]: x }));
  const go = async () => { setBusy(true); try { await f.submit(v); } finally { setBusy(false); } };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ ...card, width: 560, maxWidth: 'calc(100vw - 32px)', maxHeight: '90vh', overflowY: 'auto', marginBottom: 0 }} onClick={e => e.stopPropagation()}>
        <div style={titleRow}><h3 style={gridTitle}>{f.title}</h3></div>
        {f.note && <div style={{ fontSize: '12px', color: '#92400e', backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 4, padding: '6px 8px', marginBottom: 10, whiteSpace: 'pre-line' }}>{f.note}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
          {f.fields.map(fd => (
            <div key={fd.k} style={{ gridColumn: fd.full ? '1 / -1' : undefined }}>
              {fd.type === 'check' ? (
                <label style={{ fontSize: '12px', color: colors.body, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={!!v[fd.k]} onChange={e => set(fd.k, e.target.checked)} /> {fd.label}
                </label>
              ) : (
                <>
                  <span style={lbl}>{fd.label}</span>
                  {fd.type === 'select' ? (
                    <select style={inp} value={v[fd.k] ?? ''} onChange={e => set(fd.k, e.target.value)}>
                      {fd.opts!.map(([val, nm]) => <option key={val} value={val}>{nm}</option>)}
                    </select>
                  ) : (
                    <input style={inp} type={fd.type === 'number' ? 'number' : fd.type === 'date' ? 'date' : fd.type === 'password' ? 'password' : 'text'}
                      value={v[fd.k] ?? ''} onChange={e => set(fd.k, e.target.value)} autoComplete="off" />
                  )}
                </>
              )}
              {fd.hint && <span style={{ ...gridHint, fontSize: '11px' }}>{fd.hint}</span>}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
          <button style={btn('#6b7280')} onClick={onClose}>취소</button>
          <button style={btn(busy ? colors.btnDisabled : colors.btnPrimary)} disabled={busy} onClick={go}>{busy ? '저장 중…' : '저장'}</button>
        </div>
      </div>
    </div>
  );
};

const AccountAdmin: React.FC = () => {
  const gridRef = useRef<AgGridReact<Row>>(null);
  const [tree, setTree] = useState<Tree | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selPf, setSelPf] = useState<number | null>(null);
  const [fUser, setFUser] = useState<number | ''>('');
  const [fGrp, setFGrp] = useState<number | ''>('');
  const [fState, setFState] = useState<'' | 'on' | 'off'>('');   // 전체 · 사용중 · 정지
  const [form, setForm] = useState<FormState | null>(null);
  const [log, setLog] = useState<LogRow[] | null>(null);         // null = 이력 창 닫힘
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const t = await getMainAxios().get('/api/v1/acct-admin/tree');
      setTree(t.data as Tree);
      setError(null);
    } catch (e: any) { setError(errMsg(e, '조회 실패')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const userNm = useCallback((id: number) => tree?.users.find(u => u.id === id)?.name ?? `#${id}`, [tree]);
  const grpOf = useCallback((id?: number | null) => tree?.groups.find(x => x.id === id) ?? null, [tree]);

  const rows = useMemo<Row[]>(() => {
    if (!tree) return [];
    const mk = (p: Period, g: Group | null): Row => ({
      ...p, ownerId: g?.userId ?? 0, ownerNm: g ? userNm(g.userId) : '',
      userNms: [...new Set(tree.grants.filter(x => x.pfId === p.pfId).map(x => x.userId))].map(userNm).join(', '),
    });
    return [...tree.groups.flatMap(g => g.periods.map(p => mk(p, g))), ...tree.ungrouped.map(p => mk(p, null))]
      .filter(r => fState === '' || (fState === 'on') === r.isActive)
      .filter(r => fGrp === '' || r.grpId === fGrp)
      .filter(r => fUser === '' || r.ownerId === fUser || tree.grants.some(x => x.userId === fUser && x.pfId === r.pfId))
      .sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.pfId - b.pfId);
  }, [tree, fState, fGrp, fUser, userNm]);

  // ── 편집 그리드 ──
  const ed = useGridEdits<Row>(r => r.pfId);
  // 새 행은 고른 그룹에 따라 브로커·실전/모의·소유자를 보여 준다
  const viewRows = useMemo(() => ed.apply(rows).map(r => {
    if (r.pfId > 0) return r;
    const g = grpOf(r.grpId);
    return g ? { ...r, broker: g.broker, isReal: g.isReal, ownerId: g.userId, ownerNm: userNm(g.userId) } : r;
  }), [rows, ed.apply, grpOf, userNm]);
  const sel = viewRows.find(r => r.pfId === selPf && r.pfId > 0) ?? null;

  const addRow = () => {
    if (!tree?.groups.length) { alert('그룹을 먼저 추가하세요.'); return; }
    const g = grpOf(fGrp !== '' ? fGrp : sel?.grpId) ?? tree.groups[0];
    const last = g.periods[0];
    ed.add(id => ({
      pfId: id, idNm: '', grpId: g.id, accountNo: '', htsId: last?.htsId ?? '', broker: g.broker, isReal: g.isReal,
      acntType: g.acntType, isActive: true, initD: today(), endD: g.isReal ? null : addMonths(today(), 3),
      initAmt: last?.initAmt ?? 0, nSlots: 0, allocSum: 0, ownerId: g.userId, ownerNm: userNm(g.userId), userNms: '',
      appKeyNew: '', appSecretNew: '',
    }));
  };

  const LABEL: Record<string, string> = {
    idNm: '이름', grpId: '그룹', htsId: 'HTS ID', acntType: '상품', initD: '시작', endD: '종료', initAmt: '원금',
    isActive: '상태', accountNo: '계좌번호', appKeyNew: 'API Key', appSecretNew: 'API Secret',
  };
  const fmtVal = (k: string, v: any) => k === 'grpId' ? (grpOf(v)?.name ?? '-') : k === 'acntType' ? (v === 'fut' ? '선물옵션' : '주식')
    : k === 'isActive' ? (v ? '사용중' : '정지') : k === 'initAmt' ? won(Number(v)) : k.startsWith('app') ? '(새 값)' : (v || '(비움)');

  const saveAll = async () => {
    const { added, changed } = ed.pending;
    if (!added.length && !changed.length) return;
    // 새 행 필수값 — 저장 전에 거른다
    const bad = added.flatMap(r => {
      const miss = [!r.grpId && '그룹', !r.accountNo?.trim() && '계좌번호', !r.appKeyNew?.trim() && 'API Key', !r.appSecretNew?.trim() && 'API Secret', !r.initD && '시작'].filter(Boolean);
      return miss.length ? [`${acctNm(r)}: ${miss.join('·')} 필요`] : [];
    });
    if (bad.length) { alert(`새 계좌의 필수 칸을 채우세요.\n\n${bad.join('\n')}`); return; }

    const lines = [
      ...added.map(r => `+ ${acctNm(r)} ${r.accountNo} (${grpOf(r.grpId)?.name})`),
      ...changed.map(id => {
        const r = rows.find(x => x.pfId === id);
        const ch = Object.entries(ed.edits[id]).map(([k, v]) => `${LABEL[k] ?? k} → ${fmtVal(k, v)}`).join(', ');
        return `${r ? acctNm(r) : id}${r?.isReal ? ' [실전]' : ''}: ${ch}`;
      }),
    ];
    const memo = prompt(`${lines.length}건을 저장합니다.\n\n${lines.join('\n')}\n\n사유(이력에 남음, 비워도 됨):`, '');
    if (memo === null) return;

    setSaving(true);
    const fail: string[] = []; const ok: number[] = []; const todo: string[] = [];
    for (const r of added) {
      try {
        const res = await getMainAxios().post('/api/v1/acct-admin/periods', {
          grpId: r.grpId, idNm: r.idNm || null, accountNo: r.accountNo.trim(), appKey: r.appKeyNew, appSecret: r.appSecretNew,
          htsId: r.htsId || null, initD: r.initD, endD: r.endD || null, initAmt: Number(r.initAmt) || 0, acntType: r.acntType, memo: memo || null,
        });
        ok.push(r.pfId);
        if (res.data?.todo) todo.push(`${res.data.pfId}: ${res.data.todo}`);
      } catch (err: any) { fail.push(`${acctNm(r)}: ${errMsg(err, '등록 실패')}`); }
    }
    for (const id of changed) {
      const e = ed.edits[id] as any;
      try {
        const body: any = { memo: memo || null };
        let any = false;
        for (const k of ['idNm', 'htsId', 'acntType', 'initD', 'grpId', 'isActive']) if (k in e) { body[k] = e[k] ?? (k === 'idNm' || k === 'htsId' ? '' : null); any = true; }
        if ('initAmt' in e) { body.initAmt = Number(e.initAmt) || 0; any = true; }
        if ('endD' in e) { if (e.endD) body.endD = e.endD; else body.clearEndD = true; any = true; }
        if (any) await getMainAxios().post(`/api/v1/acct-admin/periods/${id}/update`, body);
        if (e.appKeyNew?.trim() || e.appSecretNew?.trim()) {
          const res = await getMainAxios().post(`/api/v1/acct-admin/periods/${id}/key`, { appKey: e.appKeyNew?.trim() || null, appSecret: e.appSecretNew?.trim() || null, memo: memo || null });
          if (res.data?.todo) todo.push(`${id}: ${res.data.todo}`);
        }
        ok.push(id);
      } catch (err: any) { fail.push(`${id}: ${errMsg(err, '저장 실패')}`); }
    }
    setSaving(false);
    ed.drop(ok);
    await load();
    alert([
      fail.length ? `저장 ${ok.length}건 · 실패 ${fail.length}건 (그대로 남음)\n${fail.join('\n')}` : `저장했습니다 (${ok.length}건).`,
      todo.length ? `\n토큰 안내\n${todo.join('\n')}` : '',
    ].join('\n'));
  };

  /** [삭제] — 새 행은 지우고, 저장된 계좌는 정지(물리 삭제 금지). 바로 반영한다 */
  const deleteChecked = async () => {
    const chk = gridRef.current?.api.getSelectedRows() ?? [];
    if (!chk.length) { alert('지울 행을 체크하세요.'); return; }
    const fresh = chk.filter(r => r.pfId < 0);
    const saved = chk.filter(r => r.pfId > 0 && r.isActive);
    ed.drop(fresh.map(r => r.pfId));
    if (!saved.length) return;
    const memo = prompt(`계좌를 정지합니다 (데이터는 남고, 주문·체결이 멈춥니다).\n\n${saved.map(r => `${acctNm(r)} ${r.accountNo}${r.isReal ? ' [실전]' : ''}`).join('\n')}\n\n사유:`, '');
    if (memo === null) return;
    const fail: string[] = [];
    for (const r of saved) {
      try { await getMainAxios().post(`/api/v1/acct-admin/periods/${r.pfId}/update`, { isActive: false, memo: memo || '정지' }); }
      catch (err: any) { fail.push(`${r.pfId}: ${errMsg(err, '정지 실패')}`); }
    }
    await load();
    if (fail.length) alert(fail.join('\n'));
  };

  const openLog = async () => {
    if (!sel) { alert('계좌를 클릭해 고르세요.'); return; }
    try { setLog((await getMainAxios().get(`/api/v1/acct-admin/periods/${sel.pfId}/log`)).data as LogRow[]); }
    catch (e: any) { alert(errMsg(e, '이력 조회 실패')); }
  };

  const post = async (url: string, body: any, okMsg: string) => {
    try {
      const res = await getMainAxios().post(url, body);
      const d = res.data as any;
      alert([okMsg, d?.todo].filter(Boolean).join('\n\n'));
      setForm(null);
      await load();
      return d;
    } catch (e: any) { alert(errMsg(e, '저장 실패')); return null; }
  };

  const userOpts: [string, string][] = (tree?.users ?? []).filter(u => u.role > 0).map(u => [String(u.id), `${u.name} (${u.email})`]);
  const typeOpts: [string, string][] = [['stk', '주식'], ['fut', '선물옵션']];

  const openGroup = (g?: Group | null) => setForm({
    title: g ? `그룹 수정 — ${g.name}` : '계좌 그룹 추가',
    note: g ? (g.periods.length ? '계좌가 있는 그룹은 브로커·실전 여부를 바꿀 수 없습니다.' : undefined)
      : '그룹 = 기간이 바뀌어도 남는 단위 (예: "모의 주식 (KIS)"). 대회마다 바뀌는 계좌는 위 그리드에 [행 추가] 하거나 [새 기간으로 갱신] 합니다.',
    fields: [
      { k: 'name', label: '그룹 이름', full: true },
      { k: 'userId', label: '소유 사용자', type: 'select', opts: userOpts },
      { k: 'broker', label: '브로커', type: 'select', opts: [['kis', 'KIS'], ['kiwoom', '키움'], ['sim', 'SIM 가상체결']] },
      { k: 'isReal', label: '실전/모의', type: 'select', opts: [['false', '모의'], ['true', '실전']] },
      { k: 'acntType', label: '구분 (직접 선택)', type: 'select', opts: typeOpts },
      { k: 'memo', label: '메모', full: true },
      ...(g ? [{ k: 'isActive', label: '사용중', type: 'check' as const }] : []),
    ],
    values: g ? { ...g, userId: String(g.userId), isReal: String(g.isReal) }
      : { name: '', userId: String(fUser !== '' ? fUser : 1), broker: 'kis', isReal: 'false', acntType: 'stk', memo: '' },
    submit: async v => {
      await post('/api/v1/acct-admin/groups', {
        id: g?.id ?? null, userId: Number(v.userId), name: v.name, broker: v.broker, isReal: v.isReal === 'true',
        acntType: v.acntType, memo: v.memo || null, isActive: g ? !!v.isActive : true,
      }, g ? '그룹을 수정했습니다.' : '그룹을 추가했습니다.');
    },
  });

  const openRollover = (p: Row) => setForm({
    title: `새 기간으로 갱신 — ${acctNm(p)} (${p.accountNo})`,
    note: '새 계좌를 같은 그룹에 등록하고, 이 계좌의 전략 슬롯과 외부 사용자 연결을 새 계좌로 복사합니다 (MANUAL·ENS·TA 시스템 슬롯은 주문 때 자동 생성).\n토큰은 06:00 배치가 받습니다 — 오늘 시작이면 수동 발급 + 주문 서비스 재시작이 필요합니다.',
    fields: [
      { k: 'idNm', label: '이름 (예: 모의주식-2611)' },
      { k: 'accountNo', label: '새 계좌번호' },
      { k: 'htsId', label: 'HTS ID' },
      { k: 'appKey', label: 'API App Key', full: true },
      { k: 'appSecret', label: 'API App Secret', type: 'password', full: true },
      { k: 'initD', label: '시작일', type: 'date' },
      { k: 'endD', label: '종료일', type: 'date' },
      { k: 'initAmt', label: '원금 (원)', type: 'number' },
      { k: 'slotScale', label: '슬롯 배정자금', type: 'select', opts: [['byInitAmt', '원금 비율로 환산'], ['same', '그대로']] },
      { k: 'closeOld', label: `이전 계좌(${acctNm(p)})`, type: 'select', opts: [['none', '그대로 둠 (같이 운용)'], ['atEndD', '새 시작일 전날 종료 (만료 배치가 정지)'], ['now', '지금 정지']] },
      { k: 'copySlots', label: '전략 슬롯 복사', type: 'check' },
      { k: 'copyGrants', label: '외부 사용자·권한 복사', type: 'check' },
      { k: 'memo', label: '메모 (이력에 남음)', full: true },
    ],
    values: { idNm: nextNm(p.idNm, today()), accountNo: '', htsId: p.htsId ?? '', appKey: '', appSecret: '', initD: today(), endD: addMonths(today(), 3), initAmt: p.initAmt,
      slotScale: 'byInitAmt', closeOld: 'atEndD', copySlots: true, copyGrants: true, memo: '' },
    submit: async v => {
      const d = await post(`/api/v1/acct-admin/periods/${p.pfId}/rollover`, {
        idNm: v.idNm || null, accountNo: v.accountNo, appKey: v.appKey, appSecret: v.appSecret, htsId: v.htsId || null,
        initD: v.initD, endD: v.endD || null, initAmt: Number(v.initAmt) || 0,
        copySlots: !!v.copySlots, slotScale: v.slotScale, copyGrants: !!v.copyGrants, closeOld: v.closeOld, memo: v.memo || null,
      }, '갱신했습니다.');
      if (d?.pfId) { alert(`새 계좌 ${d.pfId} — 슬롯 ${d.slots}개 · 권한 ${d.grants}건 복사 (배정 ×${Number(d.scale).toFixed(2)})`); setSelPf(d.pfId); }
    },
  });

  // ── 열 — ✎ = 바로 수정. 새 행에서만 고치는 칸: 계좌번호 ──
  const isNew = (p: any) => !!p.data && p.data.pfId < 0;
  const columnDefs = useMemo<ColDef<Row>[]>(() => [
    { field: 'pfId', headerName: 'ID', width: 62, pinned: 'left', cellStyle: { textAlign: 'right', fontWeight: 600 }, valueFormatter: p => p.value < 0 ? '새' : String(p.value) },
    { field: 'idNm', headerName: '이름 ✎', width: 120, pinned: 'left', editable: true, valueFormatter: p => p.value ?? '' },
    {
      field: 'grpId', headerName: '그룹 ✎', width: 130, editable: true,
      valueFormatter: p => grpOf(p.value)?.name ?? '(그룹 없음)', cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: (tree?.groups ?? []).map(x => x.id) },
    },
    { field: 'ownerNm', headerName: '소유자', width: 70 },
    { headerName: '브로커', width: 105, sortable: false, cellRenderer: (p: any) => p.data ? <BrokerBadge p={p.data} /> : null },
    {
      field: 'acntType', headerName: '상품 ✎', width: 80, editable: true, cellStyle: { textAlign: 'center' },
      valueFormatter: p => p.value === 'fut' ? '선물옵션' : '주식', cellEditor: 'agSelectCellEditor', cellEditorParams: { values: ['stk', 'fut'] },
    },
    { field: 'accountNo', headerName: '계좌번호', width: 110, editable: isNew, cellStyle: { textAlign: 'center' },
      headerTooltip: '새 행에서만 입력 — 저장된 계좌번호는 바꾸지 않는다(대회가 바뀌면 새 기간으로 갱신)' },
    { field: 'htsId', headerName: 'HTS ID ✎', width: 90, editable: true, valueFormatter: p => p.value ?? '' },
    {
      field: 'appKeyNew', headerName: 'API Key ✎', width: 110, editable: true,
      headerTooltip: '새 키를 넣으면 교체된다. 지금 키는 앞 6자만 보인다',
      valueFormatter: p => p.value ? `${String(p.value).slice(0, 6)}… (새 값)` : (p.data?.appKey ?? (isNew(p) ? '(입력)' : '')),
    },
    {
      field: 'appSecretNew', headerName: 'API Secret ✎', width: 110, editable: true,
      valueFormatter: p => p.value ? '•••••• (새 값)' : isNew(p) ? '(입력)' : '••••••',
    },
    { field: 'initD', headerName: '시작 ✎', width: 105, editable: true, cellEditor: 'agDateStringCellEditor', cellStyle: { textAlign: 'center' } },
    { field: 'endD', headerName: '종료 ✎', width: 105, editable: true, cellEditor: 'agDateStringCellEditor', cellStyle: { textAlign: 'center' }, valueFormatter: p => p.value || '미정' },
    {
      field: 'daysLeft', headerName: '남음', width: 65, cellStyle: { textAlign: 'center' },
      cellRenderer: (p: any) => p.value == null ? '' : p.value < 0 ? <span style={badge(colors.muted, colors.dividerLight)}>종료</span>
        : <span style={badge(p.value <= 7 ? colors.up : colors.body, p.value <= 7 ? colors.buyBg : colors.dividerLight)}>D-{p.value}</span>,
    },
    { field: 'initAmt', headerName: '원금 ✎', width: 120, editable: true, cellEditor: 'agNumberCellEditor', cellEditorParams: { min: 0 }, cellStyle: { textAlign: 'right' }, valueFormatter: p => won(p.value) },
    { headerName: '슬롯 · 배정', width: 135, cellStyle: { textAlign: 'right' }, valueGetter: p => p.data && p.data.pfId > 0 ? `${p.data.nSlots}개 · ${won(p.data.allocSum)}` : '' },
    { field: 'userNms', headerName: '사용자', width: 150, tooltipField: 'userNms' },
    {
      field: 'isActive', headerName: '상태 ✎', width: 80, editable: true, cellStyle: { textAlign: 'center' },
      cellEditor: 'agSelectCellEditor', cellEditorParams: { values: [true, false] }, valueFormatter: p => p.value ? '사용중' : '정지',
      cellRenderer: (p: any) => <span style={{ color: p.value ? '#16a34a' : colors.up, fontWeight: 600 }}>{p.value ? '사용중' : '정지'}</span>,
    },
    {
      field: 'tokenToday', headerName: '토큰', width: 60, cellStyle: { textAlign: 'center' },
      cellRenderer: (p: any) => p.value == null ? '-' : p.value ? <span style={{ color: '#16a34a' }}>✓</span> : <span style={{ color: colors.up }}>없음</span>,
    },
    { field: 'prevPfId', headerName: '이전', width: 55, cellStyle: { textAlign: 'right' }, valueFormatter: p => p.value ? String(p.value) : '' },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [tree, grpOf]);

  return (
    <div style={page}>
      <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <span style={toolbarLabel}>계좌·전략 관리 {loading && '(조회 중...)'}</span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select style={selectSm} value={fUser} onChange={e => setFUser(e.target.value === '' ? '' : Number(e.target.value))}>
            <option value="">사용자 전체</option>
            {(tree?.users ?? []).filter(u => u.role > 0).map(u => <option key={u.id} value={u.id}>{u.name} ({ROLE_NM[u.role] ?? u.role})</option>)}
          </select>
          <select style={selectSm} value={fGrp} onChange={e => setFGrp(e.target.value === '' ? '' : Number(e.target.value))}>
            <option value="">그룹 전체</option>
            {(tree?.groups ?? []).map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
          <select style={selectSm} value={fState} onChange={e => setFState(e.target.value as '' | 'on' | 'off')}>
            <option value="">상태 전체</option>
            <option value="on">사용중</option>
            <option value="off">정지</option>
          </select>
          <button style={btnPrimary} onClick={load}>조회</button>
        </div>
      </div>
      {error && <div style={{ ...card, borderColor: colors.up, color: '#991b1b', fontSize: '12px' }}>{error}</div>}

      {/* 계좌 — 조회·추가·수정·정지 */}
      <div style={cardGrid}>
        <div style={{ ...titleRow, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={gridTitle}>계좌 <span style={gridHint}>({rows.length}건 · 클릭: 아래에 그 계좌의 전략 슬롯)</span></h3>
          <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {ed.count > 0 && <span style={{ fontSize: '12px', color: '#b45309', fontWeight: 600 }}>저장 안 한 변경 {ed.count}건</span>}
            <button style={btn('#22c55e')} onClick={addRow}>행 추가</button>
            <button style={btn(ed.count && !saving ? '#16a34a' : colors.btnDisabled)} disabled={!ed.count || saving} onClick={saveAll}>{saving ? '저장 중…' : '변경 저장'}</button>
            <button style={btn(ed.count ? '#6b7280' : colors.btnDisabled)} disabled={!ed.count} onClick={ed.reset}>되돌리기</button>
            <button style={btn(colors.up)} onClick={deleteChecked}>삭제(정지)</button>
            <span style={{ width: 1, height: 20, backgroundColor: colors.divider, margin: '0 2px' }} />
            <button style={btn(sel && !sel.isReal && sel.broker !== 'sim' && sel.isActive ? '#0d9488' : colors.btnDisabled)}
              disabled={!sel || sel.isReal || sel.broker === 'sim' || !sel.isActive} onClick={() => sel && openRollover(sel)}
              title="클릭한 모의 계좌를 새 대회 계좌로 — 슬롯·외부 사용자 복사">새 기간으로 갱신</button>
            <button style={btn(sel ? '#6b7280' : colors.btnDisabled)} disabled={!sel} onClick={openLog}>변경 이력</button>
            <button style={btn('#6b7280')} onClick={() => openGroup()}>그룹 추가</button>
            <button style={btn(sel?.grpId ? '#6b7280' : colors.btnDisabled)} disabled={!sel?.grpId} onClick={() => openGroup(grpOf(sel?.grpId))}>그룹 수정</button>
          </span>
        </div>
        <div className="ag-theme-quartz" style={agGridBox(Math.max(140, Math.min(44 + viewRows.length * 26, 380)))}>
          <AgGridReact<Row>
            ref={gridRef}
            rowData={viewRows}
            columnDefs={columnDefs}
            // cellDataType: false — 값으로 형식을 추측하지 않는다 (날짜로 시작하는 글자를 날짜 칸으로 보는 사고, StrategySlotManager 참고)
            defaultColDef={{ resizable: true, sortable: true, cellDataType: false, cellStyle: { fontSize: '12px' }, cellClassRules: ed.cellClassRules }}
            onCellValueChanged={ed.onCellValueChanged}
            stopEditingWhenCellsLoseFocus={true}
            getRowId={r => String(r.data.pfId)}
            rowSelection={{ mode: 'multiRow', checkboxes: true, headerCheckbox: false, enableClickSelection: false }}
            selectionColumnDef={{ pinned: 'left', width: 36, resizable: false }}
            onRowClicked={e => { if (e.data && e.data.pfId > 0) setSelPf(e.data.pfId); }}
            getRowStyle={r => ed.newRowStyle(r) ?? (r.data?.pfId === selPf ? { backgroundColor: colors.rowHover }
              : r.data?.isActive === false ? { color: colors.hint } : undefined)}
          />
        </div>
        <div style={{ fontSize: '11px', color: colors.muted, marginTop: 6 }}>
          ✎ 칸은 더블클릭(또는 Enter)으로 수정 · 노란 칸 = 저장 안 한 변경 · 초록 행 = 새 계좌(그룹·계좌번호·API Key·Secret·시작 필수) ·
          API 키는 새 값을 넣을 때만 바뀐다 · [삭제] = 정지(데이터 보존) · 사용자·비밀번호는 Admin &gt; 계정관리
        </div>
      </div>

      {/* 전략 슬롯 — 위에서 클릭한 계좌 */}
      {sel
        ? <StrategySlotManager key={sel.pfId} pfId={sel.pfId} />
        : <div style={card}><span style={emptyMsg}>위 계좌 그리드에서 행을 클릭하면 그 계좌의 전략 슬롯이 나옵니다.</span></div>}

      {log && sel && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setLog(null)}>
          <div style={{ ...cardGrid, width: 900, maxWidth: 'calc(100vw - 32px)', marginBottom: 0 }} onClick={e => e.stopPropagation()}>
            <div style={titleRow}>
              <h3 style={gridTitle}>변경 이력 — {acctNm(sel)} <span style={gridHint}>(키는 앞 6자만)</span></h3>
              <button style={btn('#6b7280')} onClick={() => setLog(null)}>닫기</button>
            </div>
            <div className="ag-theme-quartz" style={agGridBox(Math.max(140, Math.min(44 + log.length * 26, 420)))}>
              <AgGridReact<LogRow> rowData={log} overlayNoRowsTemplate="이력 없음"
                defaultColDef={{ resizable: true, sortable: true, cellDataType: false, cellStyle: { fontSize: '12px' } }}
                columnDefs={[
                  { field: 'at', headerName: '시각', width: 150, valueFormatter: c => (c.value ?? '').replace('T', ' ').slice(0, 19) },
                  { field: 'field', headerName: '항목', width: 90 },
                  { field: 'oldVal', headerName: '이전', width: 160 },
                  { field: 'newVal', headerName: '이후', width: 160 },
                  { field: 'by', headerName: '누가', width: 80 },
                  { field: 'memo', headerName: '메모', flex: 1, minWidth: 180 },
                ]} />
            </div>
          </div>
        </div>
      )}

      {form && <FormModal f={form} onClose={() => setForm(null)} />}
    </div>
  );
};

export default AccountAdmin;
